import { IMAGE_CROP_QWEN_BATCH_SIZE } from '../constants/imageCropConfig';

/** 与后端 ImageCropTaskSubmitService.computeDetectBatches 一致 */
export function computeImageCropDetectBatches(
  total,
  batchSize = IMAGE_CROP_QWEN_BATCH_SIZE,
) {
  const count = Number(total) || 0;
  if (count <= 0) return 0;
  const chunk = Math.max(1, Number(batchSize) || IMAGE_CROP_QWEN_BATCH_SIZE);
  return Math.ceil(count / chunk);
}

/**
 * 从 poll 行推算当前 Qwen 识别 chunk 下标（0-based）。
 * T2 耦合：后端 detectBatchIndex 可能为 tick 内相对序号（0..chunk-1），也可能为任务全列表下标；
 * 换算时优先用 imageIds 位次纠偏，避免并发多 tick 时进度条滞后。
 */
export function resolveDetectGlobalImageIndex(imageId, imageIds, storedIndex) {
  const ids = (imageIds || []).map(String);
  const iid = String(imageId ?? '').trim();
  const pos = iid ? ids.indexOf(iid) : -1;
  const stored = Number(storedIndex);
  if (!Number.isFinite(stored)) {
    return pos >= 0 ? pos : -1;
  }
  if (pos < 0) {
    return stored;
  }
  // tick 相对序号恒小于其在全列表中的位次；全列表下标则 stored >= pos（同一张图）
  if (stored < pos) {
    return pos;
  }
  return stored;
}

export function deriveDetectBatchChunkIndex(
  rows,
  imageIds,
  batchSize = IMAGE_CROP_QWEN_BATCH_SIZE,
) {
  const ids = (imageIds || []).map(String);
  const chunk = Math.max(1, Number(batchSize) || IMAGE_CROP_QWEN_BATCH_SIZE);
  const totalBatches = computeImageCropDetectBatches(ids.length, chunk);
  if (totalBatches <= 0) return 0;

  const idSet = new Set(ids);
  let maxStartedIndex = -1;

  for (const row of rows || []) {
    if (!row || typeof row !== 'object') continue;
    const imageId = String(row.imageId ?? row.image_id ?? row.id ?? '').trim();
    if (!imageId || !idSet.has(imageId)) continue;

    const st = String(row.status ?? row.crop_status ?? '').toLowerCase();
    const started = st === 'processing' || st === 'success' || st === 'partial' || st === 'failed';
    if (!started) continue;

    const idx = resolveDetectGlobalImageIndex(
      imageId,
      ids,
      row.detectBatchIndex ?? row.detect_batch_index,
    );
    if (idx >= 0) {
      maxStartedIndex = Math.max(maxStartedIndex, idx);
    }
  }

  if (maxStartedIndex < 0) return 0;
  const chunkIndex = Math.floor(maxStartedIndex / chunk);
  return Math.min(Math.max(0, chunkIndex), totalBatches - 1);
}

export function createIdleStudioProgress() {
  return {
    active: false,
    phase: 'idle',
    uploadDone: 0,
    uploadTotal: 0,
    detectBatchIndex: 0,
    detectBatches: 0,
    cropSuccess: 0,
    cropFailed: 0,
    cropTotal: 0,
  };
}

export function createUploadStudioProgress(uploadDone = 0, uploadTotal = 0) {
  const done = Math.max(0, Number(uploadDone) || 0);
  const total = Math.max(done, Number(uploadTotal) || 0);
  return {
    active: true,
    phase: 'upload',
    uploadDone: done,
    uploadTotal: total,
    detectBatchIndex: 0,
    detectBatches: 0,
    cropSuccess: 0,
    cropFailed: 0,
    cropTotal: 0,
  };
}

export function createCropStudioProgress({ total, detectBatches, uploadDone = 0, uploadTotal } = {}) {
  const cropTotal = Math.max(0, Number(total) || 0);
  const done = Math.max(0, Number(uploadDone) || 0);
  const resolvedUploadTotal = uploadTotal == null
    ? Math.max(done, cropTotal)
    : Math.max(done, Number(uploadTotal) || 0);
  return {
    active: true,
    phase: 'crop',
    uploadDone: done,
    uploadTotal: resolvedUploadTotal,
    detectBatchIndex: 0,
    detectBatches: Number(detectBatches) || computeImageCropDetectBatches(cropTotal),
    cropSuccess: 0,
    cropFailed: 0,
    cropTotal,
  };
}

export function patchStudioProgress(prev, patch) {
  return { ...prev, ...patch, active: true };
}

/** 裁剪阶段整体进度（0–100） */
export function computeStudioCropPercent(progress) {
  if (!progress?.active) return 0;
  if (progress.phase === 'upload') {
    const total = Math.max(1, Number(progress.uploadTotal) || 0);
    const done = Math.max(0, Number(progress.uploadDone) || 0);
    if (!Number(progress.uploadTotal)) return 0;
    return Math.min(100, Math.round((Math.min(done, total) / total) * 100));
  }
  const total = Math.max(1, Number(progress.cropTotal) || 1);
  const done = Math.max(0, Number(progress.cropSuccess) || 0)
    + Math.max(0, Number(progress.cropFailed) || 0);
  return Math.min(100, Math.round((done / total) * 100));
}

export function formatStudioProgressLines(progress) {
  if (!progress?.active) return [];

  const lines = [];
  const uploadDone = Math.max(0, Number(progress.uploadDone) || 0);
  const uploadTotal = Math.max(uploadDone, Number(progress.uploadTotal) || 0);
  lines.push({
    key: 'upload',
    label: '上传',
    value: uploadTotal > 0 ? `${uploadDone}/${uploadTotal}` : `${uploadDone}`,
  });

  if (progress.phase === 'crop' && progress.detectBatches > 0) {
    const batchNo = Math.min(progress.detectBatchIndex + 1, progress.detectBatches);
    lines.push({
      key: 'detect',
      label: '识别批次',
      value: `${batchNo}/${progress.detectBatches}`,
    });
  }

  if (progress.phase === 'crop' && progress.cropTotal > 0) {
    const done = Math.max(0, progress.cropSuccess) + Math.max(0, progress.cropFailed);
    const failedSuffix = progress.cropFailed > 0 ? `，失败 ${progress.cropFailed}` : '';
    lines.push({
      key: 'crop',
      label: '裁剪',
      value: `${done}/${progress.cropTotal}${failedSuffix}`,
    });
  }

  return lines;
}
